// Centralised date + number formatters. Keeps admin UI output consistent
// regardless of browser locale and avoids ad-hoc `toLocaleString()` calls
// scattered through page modules.
//
// All formatters accept either an ISO string or a Date; null/undefined/
// empty inputs render as an em-dash so table cells never collapse.

import { formatMoney, type MoneyInput } from '@apx/money';

const LOCALE = 'en-US';
const DASH = '—';

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

const DATE_FMT = new Intl.DateTimeFormat(LOCALE, DATE_OPTS);
const DATETIME_FMT = new Intl.DateTimeFormat(LOCALE, DATETIME_OPTS);
const TIME_FMT = new Intl.DateTimeFormat(LOCALE, TIME_OPTS);

const TIME_PRECISE_FMT = new Intl.DateTimeFormat(LOCALE, {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});

type Input = string | number | Date | null | undefined;

function toDate(input: Input): Date | null {
  if (input == null || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "May 20, 2026" — pass `tz` (IANA name) to render in a specific timezone instead of the browser's. */
export function fmtDate(input: Input, tz?: string): string {
  const d = toDate(input);
  if (!d) return DASH;
  return tz ? new Intl.DateTimeFormat(LOCALE, { ...DATE_OPTS, timeZone: tz }).format(d) : DATE_FMT.format(d);
}

/** "May 20, 2026, 11:08 AM" — pass `tz` (IANA name) to render in a specific timezone instead of the browser's. */
export function fmtDateTime(input: Input, tz?: string): string {
  const d = toDate(input);
  if (!d) return DASH;
  return tz ? new Intl.DateTimeFormat(LOCALE, { ...DATETIME_OPTS, timeZone: tz }).format(d) : DATETIME_FMT.format(d);
}

/** "11:08 AM" — for in-day timestamps where date is implied by context. Pass
 *  `tz` (IANA name) to render in a specific timezone instead of the browser's. */
export function fmtTime(input: Input, tz?: string): string {
  const d = toDate(input);
  if (!d) return DASH;
  return tz ? new Intl.DateTimeFormat(LOCALE, { ...TIME_OPTS, timeZone: tz }).format(d) : TIME_FMT.format(d);
}

/** "11:08:39 AM" — when seconds matter (round timing, telemetry). */
export function fmtTimePrecise(input: Input): string {
  const d = toDate(input);
  return d ? TIME_PRECISE_FMT.format(d) : DASH;
}

/** Returns ISO string suitable for HTML `title` attribute (full timestamp on hover). */
export function fmtIsoTitle(input: Input): string {
  const d = toDate(input);
  return d ? d.toISOString() : '';
}

/** Relative time like "18h ago", "3d ago". Falls back to short date past a week. */
export function fmtRelative(input: Input): string {
  const d = toDate(input);
  if (!d) return DASH;
  const diff = Date.now() - d.getTime();
  if (diff < 0) return 'in the future';
  const s = Math.floor(diff / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const day = Math.floor(h / 24);
  if (day < 7) return `${day}d ago`;
  return fmtDate(d);
}

/** "May 20, 2026, 11:08 AM · 18h ago" — combined absolute + relative. */
export function fmtDateTimeRelative(input: Input): string {
  const d = toDate(input);
  if (!d) return DASH;
  return `${DATETIME_FMT.format(d)} · ${fmtRelative(d)}`;
}

/**
 * A money amount, in the currency it is counted in.
 *
 * Every amount the API sends — balance, stake, payout, limit, GGR — is an
 * integer count of its currency's MINOR unit: USD in cents (12345 is 123.45),
 * every other currency in whole units. Exact and grouped, never compact, with
 * every decimal the currency has: "1,234.56" in USD, "169,000" in IDR.
 *
 * Without a currency the integer is printed as it is (`fmtNumber`). That is
 * only right for a figure that adds up several currencies and so has no unit;
 * pass the currency whenever the amount has one.
 */
export function fmtMoney(
  value: MoneyInput,
  currency: string | null | undefined,
  opts: { signed?: boolean } = {},
): string {
  if (!currency) {
    const body = fmtNumber(value);
    return opts.signed && isPositive(value) ? `+${body}` : body;
  }
  return formatMoney(value, currency, { compact: false, signed: opts.signed ?? false });
}

function isPositive(value: MoneyInput): boolean {
  try {
    return BigInt(value) > 0n;
  } catch {
    return Number(value) > 0;
  }
}

/** Numeric formatters — counts, and amounts whose currency is not known. For
 *  an amount with a currency, use `fmtMoney`. */
export function fmtNumber(value: number | string | bigint): string {
  if (typeof value === 'bigint') return value.toLocaleString(LOCALE);
  if (typeof value === 'number') return value.toLocaleString(LOCALE);
  // Strings are currency-unit ints (1:1) from the API. Use BigInt to avoid precision
  // loss; fall back to Number for non-integer strings.
  try {
    return BigInt(value).toLocaleString(LOCALE);
  } catch {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString(LOCALE) : value;
  }
}

// ─────────── Byte + duration formatters (Redis monitoring, #448)

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

/**
 * "412 MB", "1.02 GB", "0 B".
 *
 * Base-1024 with KB/MB/GB labels — deliberately matching what
 * `redis-cli INFO` (used_memory_human) and `docker stats` print, so an
 * operator cross-checking this page against a shell sees the same number.
 * Precision shrinks as the mantissa grows so a column of these stays a
 * stable width.
 */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return DASH;
  const sign = bytes < 0 ? '-' : '';
  let n = Math.abs(bytes);
  if (n < 1024) return `${sign}${Math.round(n)} B`;
  let unit = 0;
  while (n >= 1024 && unit < BYTE_UNITS.length - 1) {
    n /= 1024;
    unit++;
  }
  const decimals = n < 10 ? 2 : n < 100 ? 1 : 0;
  return `${sign}${n.toFixed(decimals)} ${BYTE_UNITS[unit]}`;
}

/** Signed byte delta: "+412 MB", "-1.02 GB", "no change". */
export function fmtBytesDelta(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return DASH;
  if (bytes === 0) return 'no change';
  return `${bytes > 0 ? '+' : ''}${fmtBytes(bytes)}`;
}

/**
 * Coarse two-unit duration from milliseconds: "3d 5h", "2h 14m", "8m", "12s".
 *
 * Duplicates the private helper in pages/system-health.ts. That page is
 * working and untouched here on purpose — refactoring it into this shared
 * copy is pure risk inside a feature change, so it stays a known duplicate.
 */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return DASH;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return h > 0 && m % 60 > 0 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 > 0 ? `${d}d ${h % 24}h` : `${d}d`;
}
